import * as React from 'react';

import { CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Input, Modal } from 'antd';

import { useCurrencies } from 'v2/hooks/useCurrencies';
import type { Currency } from 'v2/interfaces/currency';

type ChooseCurrenciesModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  selectedCurrencies: Currency[];
  addCurrencies: (currencies: Currency[]) => void;
};

function ChooseCurrenciesModal({
  isOpen,
  closeModal,
  selectedCurrencies: selectedCurrenciesProp = [],
  addCurrencies
}: ChooseCurrenciesModalProps) {
  const [search, setSearch] = React.useState('');
  const currenciesQuery = useCurrencies();
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<Currency[]>([]);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

  function handleCurrencyClick(currency: Currency) {
    const selectedCurrenciesIds = selectedCurrencies.map(selectedCurrency => selectedCurrency.id);

    if (selectedCurrenciesIds.includes(currency.id)) {
      setSelectedCurrencies(selectedCurrencies.filter(selectedCurrency => selectedCurrency.id !== currency.id));
      return;
    }

    setSelectedCurrencies([...selectedCurrencies, currency]);
  }

  function handleAddButtonClick() {
    setSelectedCurrencies([]);
    addCurrencies(selectedCurrencies);
    closeModal();
    setTimeout(() => setSearch(''), 500);
  }

  function handleClose() {
    setSelectedCurrencies([]);
    closeModal();
    setTimeout(() => setSearch(''), 500);
  }

  const filteredCurrencies = React.useMemo(() => {
    const noBRLCurrencies = currenciesQuery.data?.filter(currency => currency.code !== 'BRL');
    const notAlreadySelectedCurrencies = noBRLCurrencies?.filter(currency => {
      const selectedCurrenciesIds = selectedCurrenciesProp.map(selectedCurrency => selectedCurrency.id);
      return !selectedCurrenciesIds.includes(currency.id);
    });
    if (!search) return notAlreadySelectedCurrencies;
    return notAlreadySelectedCurrencies.filter(currency => {
      const codeMatches = currency.code.toLowerCase().includes(search.toLowerCase());
      const nameMatches = currency.name.toLowerCase().includes(search.toLowerCase());
      return codeMatches || nameMatches;
    });
  }, [currenciesQuery.data, search, selectedCurrenciesProp]);

  return (
    <Modal
      open={isOpen}
      width={isMobile ? '100%' : 600}
      title={<strong>Escolher moedas</strong>}
      onCancel={handleClose}
      className={!isMobile ? '[&_.ant-modal-body]:h-[400px] [&_.ant-modal-body]:overflow-auto' : ''}
      footer={
        <Button
          id='add-currencies-button'
          type='primary'
          onClick={handleAddButtonClick}
          disabled={!selectedCurrencies.length}
        >
          Adicionar moedas {!!selectedCurrencies.length && `(${selectedCurrencies.length})`}
        </Button>
      }
    >
      <Input
        id='search-currencies'
        prefix={<SearchOutlined style={{ fontSize: 20 }} />}
        suffix={
          !!search && (
            <Button type='text' size='small' aria-label='Limpar busca' onClick={() => setSearch('')} disabled={!search}>
              <CloseCircleOutlined style={{ fontSize: 20 }} />
            </Button>
          )
        }
        placeholder='Buscar moedas'
        value={search}
        onChange={event => setSearch(event.target.value)}
      />
      <div className='mt-2'>
        {filteredCurrencies?.map(currency => (
          <React.Fragment key={currency.id}>
            <div className='flex cursor-pointer items-center py-2' onClick={() => handleCurrencyClick(currency)}>
              <Checkbox
                id={`currency-${currency.code}`}
                checked={selectedCurrencies.includes(currency)}
                className='mr-2!'
              />
              <label htmlFor={`currency-${currency.code}`}>
                {currency.code} ({currency.name})
              </label>
            </div>
            <Divider className='my-0!' />
          </React.Fragment>
        ))}
        {filteredCurrencies?.length === 0 && <div className='py-4 text-center'>Nenhuma moeda encontrada</div>}
      </div>
    </Modal>
  );
}

export { ChooseCurrenciesModal };
